import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCampaign } from "../api/campaigns";
import { getMintSpaceById, getMintSpaceProduct, getMintSpaceRating, getProductReviews } from "../api/mintSpace";
import { getUserProfile } from "../api/profile";
import { getBusinessCategoryLabel } from "../constants/businessCategories";
import { useOpenSpace } from "../context/SpaceGateContext";
import { useTranslation } from "../context/TranslationContext";
import { getProfileAvatarUrl } from "../utils/userDisplay";
import { PERIOD_KEYS, ReviewsRow } from "./discover/DiscoverAll";
import { campaignRateText, formatMoney, ownerName } from "./discover/campaignDisplay";
import Avatar from "./ds/Avatar";
import { STATE_LAYER } from "./ds/IconButton";

const MINTLY_HOSTS = new Set(["mintly.ge", "www.mintly.ge"]);
const ID = "([a-f0-9]{24})";
const ROUTES = [
  ["product", new RegExp(`^/mint-space/${ID}/product/${ID}/?$`)],
  ["campaign", new RegExp(`^/mint-space/${ID}/campaigns/${ID}/?$`)],
  ["campaign", new RegExp(`^/campaigns/${ID}/?$`)],
  ["space", new RegExp(`^/mint-space/${ID}/?$`)],
  ["profile", new RegExp(`^/profile/${ID}/?$`)],
];

/** The in-app path of a Mintly address (mintly.ge or this site), or null for any other site. */
export const mintlyPath = (href) => {
  try {
    const url = new URL(href);
    return MINTLY_HOSTS.has(url.hostname) || url.host === window.location.host ? url.pathname + url.search + url.hash : null;
  } catch {
    return null;
  }
};

/**
 * What a link points at inside Mintly: a product, campaign, space or profile, with its ids
 * and the in-app path. Null for anything else, including Mintly pages without a card.
 */
export const mintlyLinkTarget = (href) => {
  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (!MINTLY_HOSTS.has(url.hostname) && url.host !== window.location.host) return null;
  for (const [kind, pattern] of ROUTES) {
    const match = url.pathname.match(pattern);
    if (!match) continue;
    const ids = match.slice(1);
    const path = url.pathname + url.search;
    if (kind === "product") return { kind, path, spaceId: ids[0], productId: ids[1] };
    if (kind === "campaign") return { kind, path, campaignId: ids[ids.length - 1] };
    if (kind === "space") return { kind, path, spaceId: ids[0] };
    return { kind, path, userId: ids[0] };
  }
  return null;
};

/** Everything a card shows, loaded once per address for the session. */
const cache = new Map();
const loaders = {
  product: async ({ spaceId, productId }) => {
    const [res, reviews] = await Promise.all([
      getMintSpaceProduct(spaceId, productId),
      getProductReviews(spaceId, productId, 1).catch(() => null),
    ]);
    if (!res?.success || !res.data?.product) throw new Error("missing");
    return { ...res.data, rating: { average: reviews?.data?.average, count: reviews?.data?.total || 0 } };
  },
  space: async ({ spaceId }) => {
    const [res, rating] = await Promise.all([getMintSpaceById(spaceId), getMintSpaceRating(spaceId).catch(() => null)]);
    if (!res?.success || !res.data) throw new Error("missing");
    return { space: res.data, rating: { average: rating?.data?.averageRating, count: rating?.data?.ratingCount || 0 } };
  },
  campaign: async ({ campaignId }) => {
    const res = await getCampaign(campaignId);
    if (!res?.success || !res.data) throw new Error("missing");
    return { campaign: res.data };
  },
  profile: async ({ userId }) => {
    const res = await getUserProfile(userId);
    if (!res?.success || !res.data) throw new Error("missing");
    return { person: res.data };
  },
};
const load = (href, target) => {
  if (!cache.has(href)) cache.set(href, loaders[target.kind](target).catch(() => null));
  return cache.get(href);
};

const isImage = (src, type) => Boolean(src) && (type || "image") !== "video";

/** What each kind of card says: picture, small leading image, eyebrow, title, a line and the action. */
const describe = (target, data, { t, language }) => {
  if (target.kind === "product") {
    const { product, space } = data;
    const free = product.accessType === "free";
    const billing = free
      ? ""
      : product.billingType === "recurring"
      ? ` / ${t(PERIOD_KEYS[product.period] || PERIOD_KEYS["1m"])}`
      : ` · ${t("home.oneTime")}`;
    const cover = product.bannerImage
      ? isImage(product.bannerImage, product.bannerType) && product.bannerImage
      : isImage(product.listingBanner, product.listingBannerType) && product.listingBanner;
    return {
      cover: cover || "",
      leading: <Avatar size="l" src={space.logo} name={space.name} />,
      eyebrow: t("linkCard.product"),
      title: product.name || t("mintSpace.untitledProduct"),
      line: `${free ? t("mintSpace.free") : formatMoney(product.price ?? 0, language)}${billing} · ${space.name}`,
      rating: data.rating,
      action: t("home.getAccess"),
    };
  }
  if (target.kind === "space") {
    const { space } = data;
    const members = space.memberCount ?? (space.members?.length || 0) + 1;
    return {
      cover: space.pages?.welcome?.banner || "",
      leading: <Avatar size="l" src={space.logo} name={space.name} />,
      eyebrow: t("linkCard.space"),
      title: space.name,
      line: [
        space.category && getBusinessCategoryLabel(t, space.category),
        t(members === 1 ? "home.membersOne" : "home.membersOther", { count: members }),
      ]
        .filter(Boolean)
        .join(" · "),
      rating: data.rating,
      action: t("linkCard.openSpace"),
    };
  }
  if (target.kind === "campaign") {
    const { campaign } = data;
    const organizer = campaign.createdBy;
    const thumb = campaign.thumbnail || campaign.campaignLogo || "";
    return {
      cover: isImage(thumb, campaign.thumbnailType) ? thumb : "",
      leading: <Avatar size="l" src={getProfileAvatarUrl(organizer)} name={ownerName(organizer) || campaign.campaignName} />,
      eyebrow: t("linkCard.campaign"),
      title: campaign.campaignName,
      line: `${campaignRateText(campaign, language)} ${t("home.perThousandViews")}`,
      action: t("linkCard.viewCampaign"),
    };
  }
  const { person } = data;
  const name = person.username || person.businessName || t("common.user");
  return {
    cover: person.profile?.bannerImage || "",
    leading: <Avatar size="l" src={getProfileAvatarUrl(person)} name={name} />,
    eyebrow: t("linkCard.profile"),
    title: name,
    line: person.profile?.bio || "",
    action: t("linkCard.viewProfile"),
  };
};

/*
  The card for a link to Mintly itself. The site is built in the browser, so reading the
  page would only find "Mintly": this reads the product, space, campaign or profile instead,
  and opens it inside the app. If it can't be loaded, `fallback` (the plain link card) shows.
*/
const MintlyLinkCard = ({ href, fallback }) => {
  const { t, language } = useTranslation();
  const target = useMemo(() => mintlyLinkTarget(href), [href]);
  const navigate = useNavigate();
  const openSpace = useOpenSpace();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null });
    load(href, target).then((data) => alive && setState({ loading: false, data }));
    return () => {
      alive = false;
    };
  }, [href, target]);

  if (!state.loading && !state.data) return fallback;

  const card = state.data ? describe(target, state.data, { t, language }) : null;
  const open = (event) => {
    // A new tab or window opens the address as usual.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    event.stopPropagation();
    if (target.kind === "space" && state.data?.space) openSpace(state.data.space);
    else navigate(target.path);
  };

  return (
    <a
      href={href}
      onClick={open}
      aria-busy={state.loading}
      className={`relative flex flex-col overflow-hidden rounded-radius-md border border-separator-default bg-fill-quaternary text-left ${STATE_LAYER}`}
    >
      {card?.cover && (
        <img src={card.cover} alt="" loading="lazy" className="h-[160px] w-full object-cover min-[600px]:h-[200px]" />
      )}
      <span className="flex items-center gap-space-12 p-space-12">
        {card ? card.leading : <span className="size-12 shrink-0 animate-pulse rounded-14 bg-fill-secondary" />}
        <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
          {card ? (
            <>
              <span className="text-caps-label uppercase text-label-tertiary">{card.eyebrow}</span>
              <span className="line-clamp-2 break-words text-headline text-label-primary">{card.title}</span>
              {card.line && <span className="truncate text-footnote text-label-secondary">{card.line}</span>}
              {card.rating && (
                <ReviewsRow average={card.rating.average} count={card.rating.count} language={language} t={t} />
              )}
            </>
          ) : (
            <>
              <span className="h-[14px] w-16 animate-pulse rounded-radius-xs bg-fill-secondary" />
              <span className="h-[18px] w-40 animate-pulse rounded-radius-xs bg-fill-secondary" />
              <span className="h-[14px] w-28 animate-pulse rounded-radius-xs bg-fill-secondary" />
            </>
          )}
        </span>
        {card && (
          <span className="hidden h-button-s shrink-0 items-center rounded-radius-md bg-accent-tinted px-space-12 text-caps-button-s uppercase text-accent-tinted-label min-[600px]:inline-flex">
            {card.action}
          </span>
        )}
      </span>
    </a>
  );
};

export default MintlyLinkCard;
